import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import TopBar from "../components/Topbar";
import OnboardingChecklist from "../components/OnboardingChecklist";
import Sidebar from "../components/Sidebar";
import CallNotification from "../components/CallNotification";
import VoiceQueuePanel from "../components/VoiceQueuePanel";
import VoicePwaInstallBanner from "../components/VoicePwaInstallBanner";
import VoicePushOnboardingBanner from "../components/VoicePushOnboardingBanner";
import FutureContactDueReminder from "../components/FutureContactDueReminder";
import ScheduleDueReminder from "../components/ScheduleDueReminder";
import AutomationSurveyPromptHost from "../components/AutomationSurveyPromptHost";
import TeamChatWidget from "../components/TeamChat/TeamChatWidget";
import VirtualOfficeInviteBanner from "../components/VirtualOfficeInviteBanner";
import { TeamTypingProvider } from "../context/TeamTypingContext";
import { useWorkspaceConfig } from "../context/WorkspaceConfigContext";
import { useAuthContext } from "../context/AuthContext";
import { useSocketContext } from "../context/SocketContext";
import { useCallContext } from "../context/CallContext";
import { useOnboarding } from "../context/OnboardingContext";
import { useNotificationsOptional } from "../context/NotificationContext";
import { useEffectivePermissions, toSet, canAny } from "../hooks/useEffectivePermissions";
import { useSetupOnboardingStatus } from "../hooks/useSetupOnboardingStatus";
import { useChannels, CHANNELS_CATALOG_PAGE, CHANNELS_CATALOG_LIMIT } from "../hooks/useChannels";
import { fetchData, getConversationById, getWhatsappCloudCallRecord, postWhatsappCloudCall } from "../api/client";
import { startWhatsappCloudVoiceSession } from "../utils/whatsappCloudVoice";
import { useLazyI18nNamespaces } from "../i18n/useLazyI18nNamespaces";
import {
  PLATFORM_CONSOLE_BASE,
  PLATFORM_CONSOLE_PATH,
  isPlatformConsolePath,
} from "../pages/Platform/XprobaseConsole/platformConsoleRoutes";
import { PlatformConsoleAclProvider } from "../pages/Platform/XprobaseConsole/PlatformConsoleAclContext";
import {
  PARTNER_CONSOLE_BASE,
  PARTNER_CONSOLE_PATH,
} from "../pages/Partner/PartnerConsole/partnerConsoleRoutes";
import {
  readWorkspaceSessionDynamic,
  writeWorkspaceSessionDynamic,
} from "../utils/workspaceStorage";
import { openCallPopupWindow } from "../utils/openCallPopupWindow";
import { bootstrapWorkspaceFromUrl } from "../utils/bootstrapWorkspaceFromUrl";
import { useSyncWorkspaceQueryParam } from "../utils/useSyncWorkspaceQueryParam";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { getCallAlertSoundUrl, playAlertSoundForType, playMp3Url } from "../notifications/soundUtils";
import { TEAM_MOBILE_MEDIA_QUERY } from "../pages/Team/utils/teamUiUtils";
import { getSessionRoleIndicator, SESSION_ROLE_STRIPE_HEIGHT } from "../utils/sessionRoleIndicator";
import WorkspaceShell from "../pages/Team/components/WorkspaceShell";
import {
  resolveWorkspaceActiveSection,
  isServiceChatPath,
  isWorkspaceNavSearch,
} from "../utils/teamNavigation";

const MainLayout = ({ children }) => {
  // const [collapsed, setCollapsed] = useState(false);
  const [collapsed, setCollapsed] = useState(
      () => JSON.parse(localStorage.getItem("collapsed")) || false
  );
  const [isMobileOpen, setMobileOpen] = useState(false);
  const [showSetupInvite, setShowSetupInvite] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [voiceQueueRefreshToken, setVoiceQueueRefreshToken] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactViewport = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const location = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    bootstrapWorkspaceFromUrl(location.search);
  }, [location.search]);

  useSyncWorkspaceQueryParam();

  useLazyI18nNamespaces(location.pathname);
  const { isLoading: workspaceLoading, companyKey } = useWorkspaceConfig();
  const { userData } = useAuthContext();
  const sessionRole = useMemo(() => getSessionRoleIndicator(userData), [userData]);
  const appBarSpacerHeight = (sessionRole ? SESSION_ROLE_STRIPE_HEIGHT : 0) + (sessionRole ? 56 : 64);

  // Expõe o offset do chrome no :root para Drawers/Modals portaled (fora do MainLayout).
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--app-chrome-offset", `${appBarSpacerHeight}px`);
    return () => {
      root.style.removeProperty("--app-chrome-offset");
    };
  }, [appBarSpacerHeight]);

  const { state: onboardingState, markStepCompleted } = useOnboarding();
  const { socket } = useSocketContext();
  const {
    clearIncomingCall,
    clearOutgoingCallIfIdle,
    setIncomingCallFromSocket,
    prepareTwilioVoiceDevice,
    rememberSoftphoneIncoming,
  } = useCallContext();
  const notificationsCtx = useNotificationsOptional();
  const prepareTwilioVoiceDeviceRef = useRef(prepareTwilioVoiceDevice);
  prepareTwilioVoiceDeviceRef.current = prepareTwilioVoiceDevice;

  const { data: channelsCatalog } = useChannels(CHANNELS_CATALOG_PAGE, CHANNELS_CATALOG_LIMIT, "");
  const catalogChannels = channelsCatalog?.channels;
  const phoneChannelIdsKey = useMemo(() => {
    if (!Array.isArray(catalogChannels)) return "";
    return catalogChannels
      .filter(
        (c) => Number(c?.tipo) === 5 || String(c?.type || "").toUpperCase() === "TWILIO_PHONE"
      )
      .map((c) => Number(c.id))
      .filter(Boolean)
      .slice(0, 8)
      .join(",");
  }, [catalogChannels]);

  // Softphone Twilio: registra Device uma vez por workspace + conjunto de canais de telefone.
  useEffect(() => {
    if (!companyKey || !phoneChannelIdsKey) return undefined;
    const prepare = prepareTwilioVoiceDeviceRef.current;
    if (typeof prepare !== "function") return undefined;
    const ids = phoneChannelIdsKey.split(",").map((id) => Number(id)).filter(Boolean);
    if (!ids.length) return undefined;
    let cancelled = false;
    (async () => {
      for (const id of ids) {
        if (cancelled) break;
        try {
          await prepare(id);
        } catch (err) {
          console.warn("[MainLayout] softphone register:", id, err?.message || err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyKey, phoneChannelIdsKey]);

  useEffect(() => {
    if (!socket) return;
    const handleCallEnded = () => {
      clearIncomingCall();
      // Softphone com Call ativa: não esconder teclado em callEnded precoce.
      clearOutgoingCallIfIdle?.();
    };
    socket.on("callEnded", handleCallEnded);
    return () => socket.off("callEnded", handleCallEnded);
  }, [socket, clearIncomingCall, clearOutgoingCallIfIdle]);

  useEffect(() => {
    if (!socket) return;
    const handleVoiceQueueUpdate = () => {
      setVoiceQueueRefreshToken((n) => n + 1);
    };
    socket.on("voiceQueueUpdate", handleVoiceQueueUpdate);
    return () => socket.off("voiceQueueUpdate", handleVoiceQueueUpdate);
  }, [socket]);

  // newCall: telefonia abre popup-call no desktop; mobile/PWA fica in-app.
  useEffect(() => {
    if (!socket || !setIncomingCallFromSocket) return;
    const openIncomingCallPopup = (conversationId) => {
      openCallPopupWindow(conversationId, { navigate });
      playMp3Url(getCallAlertSoundUrl());
    };
    const handleNewCall = async (payload) => {
      try {
        const p = typeof payload === "object" && payload !== null ? payload : {};
        const raw = p.protocol ?? p.chatId ?? p.conversationId ?? p.id ?? payload;
        const protocolId = raw != null ? Number(raw) : null;
        if (!protocolId || Number.isNaN(protocolId)) return;

        // Softphone: não recria o Device aqui — isso rouba a identidade da aba já registrada.
        // O toque vem do Device.incoming + BroadcastChannel (todas as janelas).
        if (p.kind === "telephony_softphone") {
          rememberSoftphoneIncoming?.({
            leadId: protocolId,
            chatId: protocolId,
            channelId: p.channelId,
          });
          return;
        }

        // PSTN no celular do agente: abre a solicitação no app (áudio na linha móvel).
        if (p.kind === "telephony_pstn_agent" || p.reason === "pstn_agent_ring") {
          void playAlertSoundForType("voice_call");
          navigate("/chat", { state: { openConversationId: protocolId } });
          return;
        }

        // Caixa postal / transferência offline: notifica e abre a solicitação (sem popup de ligação).
        if (p.kind === "telephony_transfer" || p.reason === "offline_mailbox" || p.reason === "voicemail_left") {
          void playAlertSoundForType("voice_call");
          navigate("/chat", { state: { openConversationId: protocolId } });
          return;
        }

        const res = await getConversationById(protocolId);
        const dto = res?.data || res;
        if (!dto) return;
        const client = dto.client || {};
        const isWaCloud = p.kind === "whatsapp_cloud";
        const isWaGupshupSip = p.kind === "whatsapp_gupshup_sip";
        const conversation = {
          id: dto.id,
          name: client.name ?? dto.name ?? "",
          phoneNumber: client.phone ?? null,
          lastMessage: dto.lastMessage || "",
          timestamp: dto.timestamp,
          channel: isWaGupshupSip ? "WhatsApp (SIP)" : isWaCloud ? "WhatsApp voz" : dto.channel,
          status: dto.status,
          assignedTo: dto.userName ?? dto.assignedTo ?? null,
          unread: dto.unread || 0,
          statusLabel: "Em Atendimento",
        };
        if (isWaGupshupSip) {
          openIncomingCallPopup(conversation.id);
          return;
        }
        if (isWaCloud) {
          setIncomingCallFromSocket({
            kind: "whatsapp_cloud",
            whatsappMeta: {
              callId: p.callId,
              channelId: p.channelId,
              remoteSdp: p.remoteSdp,
              remoteSdpType: p.remoteSdpType,
            },
            conversation,
            onAccept: async () => {
              try {
                let sdp = p.remoteSdp;
                let sdpType = p.remoteSdpType;
                if ((!sdp || !sdpType) && p.channelId && p.callId) {
                  const row = await getWhatsappCloudCallRecord(p.channelId, p.callId);
                  if (row && typeof row === "object") {
                    sdp = sdp || row.remote_sdp;
                    sdpType = sdpType || row.remote_sdp_type;
                  }
                }
                if (p.channelId && p.callId && sdp) {
                  await startWhatsappCloudVoiceSession({
                    channelId: p.channelId,
                    callId: p.callId,
                    remoteSdp: sdp,
                    remoteSdpType: sdpType,
                  });
                } else {
                  console.warn("[MainLayout] Chamada WhatsApp sem SDP (socket/API).");
                }
              } catch (e) {
                console.error("[MainLayout] WhatsApp Cloud voice:", e);
              }
              openCallPopupWindow(conversation.id, { navigate });
            },
            onReject: async () => {
              if (p.channelId && p.callId) {
                try {
                  await postWhatsappCloudCall(p.channelId, {
                    messaging_product: "whatsapp",
                    call_id: p.callId,
                    action: "reject",
                  });
                } catch (e) {
                  console.warn("[MainLayout] WhatsApp reject:", e);
                }
              }
            },
          });
          return;
        }
        openIncomingCallPopup(conversation.id);
      } catch (error) {
        console.error("[MainLayout] Erro ao processar newCall:", error);
      }
    };
    socket.on("newCall", handleNewCall);
    return () => socket.off("newCall", handleNewCall);
  }, [
    socket,
    setIncomingCallFromSocket,
    navigate,
    rememberSoftphoneIncoming,
  ]);

  useEffect(() => {
    if (!socket || !notificationsCtx?.dispatchFromSocketLegacy) return;
    const handler = (payload) => notificationsCtx.dispatchFromSocketLegacy(payload);
    socket.on("notification", handler);
    return () => socket.off("notification", handler);
  }, [socket, notificationsCtx]);

  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();
  const {
    data: serverOnboarding,
    isLoading: serverOnboardingLoading,
    isError: serverOnboardingError,
  } = useSetupOnboardingStatus(
    companyKey,
    Boolean(companyKey && !workspaceLoading && !permsLoading)
  );
  const scopesSet = toSet(permData?.scopes);
  const canConfigureWorkspace = canAny(scopesSet, [
    ["companies", "view"],
    ["channels", "view"],
    ["users", "view"],
    ["types", "view"],
    ["automation_rules", "view"],
  ]);

  const hideWorkspaceChrome = useMemo(() => {
    const path = String(location.pathname || "");
    // Console master / portal do representante: sem menu e seletor do app do tenant (o header `Company`
    // pode permanecer no storage para chamadas à API mesmo com JWT de workspace).
    if (path.startsWith(PLATFORM_CONSOLE_BASE) || path.startsWith(PARTNER_CONSOLE_BASE)) {
      return true;
    }
    const scope = userData?.scope;
    if (scope !== "partner" && scope !== "platform") return false;
    // "Sem workspace definido" aqui significa: sem slug do tenant pinado nesta aba.
    try {
      const slug = String(getActiveWorkspaceSlug() || "").trim().toLowerCase();
      const isSlug = /^[a-z0-9._-]{2,32}$/.test(slug) && !/^\d+$/.test(slug);
      return !isSlug;
    } catch {
      return true;
    }
  }, [userData?.scope, location.pathname]);

  // Chat, Equipe, tarefas, agenda e conversa interna do console ocupam a área útil inteira.
  // Importante: "/chatbots" NÃO é atendimento — startsWith("/chat") pegava analytics/editor e
  // aplicava overflow:hidden (sem rolagem).
  const isWorkspaceImmersivePage = useMemo(() => {
    const path = String(location.pathname || "");
    const isServiceChat = path === "/chat" || path.startsWith("/chat/");
    if (path === "/" || isServiceChat || path.startsWith("/equipe")) return true;
    if (path === PLATFORM_CONSOLE_PATH.team || path === PARTNER_CONSOLE_PATH.team) return true;
    return false;
  }, [location.pathname]);

  const workspaceActiveSection = useMemo(
    () => resolveWorkspaceActiveSection(location.pathname),
    [location.pathname]
  );

  const isServiceChatRoute = useMemo(
    () => isServiceChatPath(location.pathname),
    [location.pathname]
  );

  const workspaceNavFromBar = useMemo(
    () => isWorkspaceNavSearch(location.search),
    [location.search]
  );

  const showWorkspaceShell = useMemo(() => {
    if (!isWorkspaceImmersivePage) return false;
    if (isServiceChatRoute) return workspaceNavFromBar;
    return true;
  }, [isWorkspaceImmersivePage, isServiceChatRoute, workspaceNavFromBar]);

  const useWorkspaceShellLayout = useMemo(() => {
    if (!showWorkspaceShell) return false;
    if (isCompactViewport) return true;
    return isServiceChatRoute && workspaceNavFromBar;
  }, [showWorkspaceShell, isCompactViewport, isServiceChatRoute, workspaceNavFromBar]);

  /** FAB da equipe conflita com a barra inferior do workspace e o composer do chat no mobile. */
  const hideTeamChatFab = useMemo(
    () => isCompactViewport && (showWorkspaceShell || isServiceChatRoute),
    [isCompactViewport, showWorkspaceShell, isServiceChatRoute]
  );

  // Controla o Drawer mobile do Sidebar.
  // Se `nextOpen` for booleano, força o estado; caso contrário, alterna.
  const toggleDrawer = useCallback((nextOpen) => {
    if (typeof nextOpen === "boolean") {
      setMobileOpen(nextOpen);
      return;
    }
    setMobileOpen((prev) => !prev);
  }, []);

  useEffect(() => {
      // Atualiza o localStorage sempre que o collapsed muda
      localStorage.setItem("collapsed", JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    setOnboardingChecked(false);
  }, [companyKey]);

  useEffect(() => {
    if (workspaceLoading || permsLoading) return;
    if (!companyKey) return;
    if (onboardingChecked) return;

    // Evita exibir em rotas específicas (ex.: página de setup)
    if (location.pathname.startsWith("/setup")) return;

    if (serverOnboardingLoading) return;

    const optOutKey = `workspace-setup-opt-out-${companyKey}`;
    const doneKey = `workspace-setup-done-${companyKey}`;
    const seenKey = `workspace-setup-seen-${companyKey}`;
    const visitedKey = `workspace-setup-visited-${companyKey}`;

    let cancelled = false;

    const checkAndMaybeShow = async () => {
      try {
        const optedOut = localStorage.getItem(optOutKey) === "1";
        if (optedOut) {
          return;
        }

        const useServer =
          !serverOnboardingError && serverOnboarding && typeof serverOnboarding.needsSetup === "boolean";
        const serverNeedsSetup = useServer && serverOnboarding.needsSetup === true;

        if (serverNeedsSetup && canConfigureWorkspace) {
          try {
            const continueLater =
              typeof sessionStorage !== "undefined" &&
              sessionStorage.getItem(`workspace-setup-continue-later-${companyKey}`) === "1";
            if (continueLater) {
              setOnboardingChecked(true);
              return;
            }
          } catch {
            /* ignore */
          }
          try {
            localStorage.removeItem(doneKey);
          } catch {
            /* ignore */
          }
          navigate("/setup", { replace: true });
          return;
        }

        const finishedSetup = localStorage.getItem(doneKey) === "1";
        if (finishedSetup) {
          return;
        }

        const hasCompletedCoreOnboarding =
          Array.isArray(onboardingState.completedSteps) &&
          onboardingState.completedSteps.includes("core-onboarding-done");
        if (hasCompletedCoreOnboarding) {
          localStorage.setItem(doneKey, "1");
          return;
        }

        const hasVisitedSetup = localStorage.getItem(visitedKey) === "1";

        if (useServer && serverOnboarding.needsSetup === false) {
          try {
            localStorage.setItem(doneKey, "1");
          } catch {
            /* ignore */
          }
          try {
            markStepCompleted("core-onboarding-done");
          } catch {
            /* ignore */
          }
          return;
        }

        let hasAnyConfigured = false;

        if (useServer) {
          const c = serverOnboarding.counts || {};
          hasAnyConfigured =
            (Number(c.companies) || 0) > 0 ||
            (Number(c.channels) || 0) > 0 ||
            (Number(c.typesRoot) || 0) > 0;
          if (!serverNeedsSetup || hasAnyConfigured) {
            try {
              localStorage.setItem(doneKey, "1");
            } catch {
              /* ignore */
            }
            try {
              markStepCompleted("core-onboarding-done");
            } catch {
              /* ignore */
            }
            return;
          }
        } else {
          const resources = [
            { key: "companies", endpoint: "/companies?page=1&limit=1" },
            { key: "channels", endpoint: "/channels?page=1&limit=1" },
            { key: "types", endpoint: "/types?page=1&limit=1" },
          ];

          const results = await Promise.all(
            resources.map((r) =>
              fetchData(r.endpoint, "GET").catch(() => null)
            )
          );

          if (cancelled) return;

          const summary = resources.reduce((acc, resMeta, idx) => {
            const res = results[idx];
            if (!res) {
              acc[resMeta.key] = false;
              return acc;
            }
            const dataArray = Array.isArray(res.data) ? res.data : [];
            const hasArrayItems = dataArray.length > 0;
            const hasTotal =
              typeof res.totalInDatabase === "number" &&
              res.totalInDatabase > 0;
            acc[resMeta.key] = hasArrayItems || hasTotal;
            return acc;
          }, {});

          hasAnyConfigured =
            summary.companies || summary.channels || summary.types;

          if (hasAnyConfigured) {
            try {
              localStorage.setItem(doneKey, "1");
            } catch {
              /* ignore */
            }
            try {
              markStepCompleted("core-onboarding-done");
            } catch {
              /* ignore */
            }
            return;
          }
        }

        if (!hasAnyConfigured && canConfigureWorkspace && !optedOut) {
          if (useServer && serverNeedsSetup) {
            navigate("/setup", { replace: true });
            return;
          }
          if (!useServer && !hasVisitedSetup) {
            navigate("/setup", { replace: true });
            return;
          }
        }

        if (!canConfigureWorkspace && !hasAnyConfigured) {
          return;
        }

        const alreadySeenThisSession =
          readWorkspaceSessionDynamic(seenKey) === "1";
        if (alreadySeenThisSession) return;

        if (hasVisitedSetup) {
          writeWorkspaceSessionDynamic(seenKey, "1");
          setShowSetupInvite(true);
        }
      } catch {
        try {
          const alreadySeenThisSession =
            readWorkspaceSessionDynamic(seenKey) === "1";
          if (!alreadySeenThisSession) {
            writeWorkspaceSessionDynamic(seenKey, "1");
            setShowSetupInvite(true);
          }
        } catch {
          setShowSetupInvite(true);
        }
      } finally {
        if (!cancelled) {
          setOnboardingChecked(true);
        }
      }
    };

    checkAndMaybeShow();

    return () => {
      cancelled = true;
    };
  }, [
    workspaceLoading,
    companyKey,
    location.pathname,
    onboardingChecked,
    onboardingState.completedSteps,
    markStepCompleted,
    serverOnboardingLoading,
    serverOnboardingError,
    serverOnboarding,
    canConfigureWorkspace,
    navigate,
  ]);

  const handleGoToSetup = () => {
    setShowSetupInvite(false);
    navigate("/setup");
  };

  const handleAdvancedMode = () => {
    if (companyKey) {
      try {
        localStorage.setItem(`workspace-setup-opt-out-${companyKey}`, "1");
      } catch {
        // falha silenciosa
      }
    }
    setShowSetupInvite(false);
  };

  const sidebarWidthPx = hideWorkspaceChrome ? 0 : isMobile ? 0 : collapsed ? 70 : 210;
  const onPlatformConsole = isPlatformConsolePath(location.pathname);

  return (
    <PlatformConsoleAclProvider enabled={onPlatformConsole}>
    <TeamTypingProvider currentUserId={userData?.id}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        maxHeight: "100dvh",
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
        "--app-chrome-offset": `${appBarSpacerHeight}px`,
        "--app-sidebar-width": `${sidebarWidthPx}px`,
      }}
    >
      {/* Reserva altura do AppBar fixo (position="fixed" no TopBar) */}
      <Box sx={{ flexShrink: 0, height: appBarSpacerHeight }} aria-hidden />
      <TopBar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        toggleDrawer={toggleDrawer}
        hideWorkspaceChrome={hideWorkspaceChrome}
      />

      {!hideWorkspaceChrome ? (
        <>
          <VoicePwaInstallBanner enabled />
          <VoicePushOnboardingBanner enabled />
          <CallNotification />
          <VoiceQueuePanel refreshToken={voiceQueueRefreshToken} />
        </>
      ) : null}
      {!hideWorkspaceChrome && <FutureContactDueReminder />}
      {!hideWorkspaceChrome && <ScheduleDueReminder />}
      <AutomationSurveyPromptHost />
      {!hideWorkspaceChrome && !hideTeamChatFab ? <TeamChatWidget /> : null}

      {/* Shell sob o topbar: position relative + filhos absolute = scrollport estável no Safari */}
      <Box
        sx={{
          position: "relative",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {!hideWorkspaceChrome && (
          <Box
            className="sidebar-scroll"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: sidebarWidthPx,
              overflowX: "hidden",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              transition: "width 0.3s ease",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              zIndex: 1,
            }}
          >
            <Sidebar
              collapsed={collapsed}
              isMobileOpen={isMobileOpen}
              toggleDrawer={toggleDrawer}
            />
          </Box>
        )}

        <Box
          className={
            isWorkspaceImmersivePage
              ? "tour-main-content tour-main-immersive"
              : "tour-main-content tour-main-scrollable"
          }
          component="main"
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: `${sidebarWidthPx}px`,
            minWidth: 0,
            minHeight: 0,
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            padding: isWorkspaceImmersivePage && isCompactViewport ? 0 : "5px",
            transition: "left 0.3s ease, padding 0.3s ease",
            ...(isWorkspaceImmersivePage
              ? {
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }
              : {
                  overflowY: "scroll",
                  overflowX: "hidden",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: "50px",
                }),
          }}
        >
          {isWorkspaceImmersivePage ? (
            useWorkspaceShellLayout ? (
              <WorkspaceShell activeSection={workspaceActiveSection}>
                {children}
              </WorkspaceShell>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {children}
              </Box>
            )
          ) : (
            children
          )}
        </Box>
      </Box>

      {/* Onboarding principal (checklist guiado) */}
      <OnboardingChecklist
        open={showSetupInvite}
        onClose={() => setShowSetupInvite(false)}
      />
      <VirtualOfficeInviteBanner />

    </Box>
    </TeamTypingProvider>
    </PlatformConsoleAclProvider>
  );
};

export default MainLayout;
